import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Check,
  FileStack,
  FlaskConical,
  ListChecks,
  MessageSquare,
  Mic,
  Video,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Progress } from "@/components/ui/progress";
import { useSidebar, SidebarMenuButton } from "@/components/ui/sidebar";
import { useOrganization } from "@/hooks/useOrganization";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import type { OnboardingStatusResponse } from "@/lib/api/onboardingStatus";

import { OnboardingStepRow } from "./OnboardingStepRow";

interface StepConfig {
  key: keyof Pick<
    OnboardingStatusResponse,
    | "knowledge_assets_uploaded"
    | "demo_studio_recorded"
    | "interview_completed"
    | "simulations_run"
    | "agent_conversation_run"
  >;
  label: string;
  route: string;
  icon: LucideIcon;
}

const STEPS: StepConfig[] = [
  {
    key: "knowledge_assets_uploaded",
    label: "Add sources",
    route: "/agent-knowledge?tab=sources",
    icon: FileStack,
  },
  {
    key: "demo_studio_recorded",
    label: "Record product tour",
    route: "/demo-studio",
    icon: Video,
  },
  {
    key: "interview_completed",
    label: "Answer questions",
    route: "/agent-knowledge?tab=interviews",
    icon: Mic,
  },
  {
    key: "simulations_run",
    label: "Simulate demos",
    route: "/evaluation/simulations",
    icon: FlaskConical,
  },
  {
    key: "agent_conversation_run",
    label: "Test your agent",
    route: "/agents",
    icon: MessageSquare,
  },
];

const dismissKey = (orgId: string) =>
  `onboarding_checklist_dismissed_${orgId}`;

export function OnboardingChecklist() {
  const { currentOrganization } = useOrganization();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const navigate = useNavigate();
  const orgId = currentOrganization?.id;

  const { data: fetched, isLoading, isError } = useOnboardingStatus(orgId);

  // If the backend endpoint isn't available yet (or errors), fall back to an
  // all-incomplete state so the checklist still shows the user what they need
  // to do. Once the endpoint returns real data, this is replaced.
  const fallback: OnboardingStatusResponse = {
    knowledge_assets_uploaded: false,
    demo_studio_recorded: false,
    interview_completed: false,
    simulations_run: false,
    agent_conversation_run: false,
    total_completed: 0,
    total_steps: 5,
    all_complete: false,
  };
  const data = fetched ?? (isError ? fallback : undefined);

  // Track dismissal — keyed per org so switching orgs re-shows the checklist
  // if that org is still incomplete.
  const [dismissed, setDismissed] = useState<boolean>(false);
  useEffect(() => {
    if (!orgId) {
      setDismissed(false);
      return;
    }
    setDismissed(localStorage.getItem(dismissKey(orgId)) === "true");
  }, [orgId]);

  const handleDismiss = () => {
    if (!orgId) return;
    localStorage.setItem(dismissKey(orgId), "true");
    setDismissed(true);
  };

  const handleNavigate = (route: string) => {
    navigate(route);
    if (isMobile) setOpenMobile(false);
  };

  const collapsed = !isMobile && state === "collapsed";

  const nextStepKey = useMemo(() => {
    if (!data) return null;
    const firstIncomplete = STEPS.find((step) => !data[step.key]);
    return firstIncomplete?.key ?? null;
  }, [data]);

  // Don't render anything on first paint while data loads — avoids footer flash.
  if (!orgId || isLoading || !data) return null;
  if (dismissed) return null;

  const progressPct = Math.round(
    (data.total_completed / data.total_steps) * 100
  );

  // Collapsed sidebar: icon-only button with tooltip. Celebratory state gets its
  // own icon to make "you're done" obvious even when collapsed.
  if (collapsed) {
    return (
      <div className="flex w-full items-center justify-center px-1 py-2">
        <SidebarMenuButton
          className="h-8 w-8 justify-center"
          tooltip={
            data.all_complete
              ? "Setup complete"
              : `Getting started — ${data.total_completed}/${data.total_steps}`
          }
          onClick={data.all_complete ? handleDismiss : undefined}
        >
          <ListChecks
            size={15}
            strokeWidth={1.75}
            className={
              data.all_complete
                ? "text-primary"
                : "text-sidebar-foreground"
            }
          />
        </SidebarMenuButton>
      </div>
    );
  }

  return (
    <div className="pb-1">
      <AnimatePresence mode="wait" initial={false}>
        {data.all_complete ? (
          <motion.div
            key="celebration"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            className="group relative mx-1 flex h-8 items-center gap-2 rounded-md px-2 py-1.5"
          >
            <span
              className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground"
              aria-hidden
            >
              <Check size={9} strokeWidth={3.5} />
            </span>
            <span className="flex-1 truncate text-[13px] font-medium leading-5 text-sidebar-accent-foreground">
              You're all set up
            </span>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="shrink-0 rounded-sm p-0.5 text-sidebar-foreground/40 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              <X size={12} />
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="checklist"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div className="mb-1 flex items-center justify-between px-3">
              <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-sidebar-foreground/50">
                Getting started
              </span>
              <span className="text-[10px] font-medium tabular-nums text-sidebar-foreground/50">
                {data.total_completed}/{data.total_steps}
              </span>
            </div>

            <div className="mb-1 px-3">
              <Progress
                value={progressPct}
                className="h-[2px] bg-sidebar-border/70"
              />
            </div>

            <div className="flex flex-col gap-px px-1">
              {STEPS.map((step) => (
                <OnboardingStepRow
                  key={step.key}
                  label={step.label}
                  icon={step.icon}
                  complete={data[step.key]}
                  next={nextStepKey === step.key}
                  onClick={() => handleNavigate(step.route)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
